import { GoogleGenAI } from "@google/genai";

/**
 * Generates a concise summary of aggregated analytics data using the Gemini API.
 * @param data The analytics data (e.g., counts, trends) to be summarized.
 * @param timeFrame A string indicating the time frame of the data (e.g., "Monthly", "Yearly").
 * @returns A promise that resolves to a string containing the AI-generated summary.
 * @throws {Error} If the API call fails.
 */
export async function generateAnalyticsSummary(data: any, timeFrame: string): Promise<string> {
    // Get API key from Vite environment variables
    const apiKey = import.meta.env.VITE_GEMINI_API_KEY || process.env.GEMINI_API_KEY;
    
    if (!apiKey || apiKey === 'undefined' || apiKey === 'your_gemini_api_key_here') {
        throw new Error('Gemini API key is not configured. Please set VITE_GEMINI_API_KEY in your .env file.');
    }

    try {
        const ai = new GoogleGenAI({ apiKey });
    
        // Construct a detailed prompt for the AI model.
        const prompt = `You are a facility management analyst. Based on the following ${timeFrame} maintenance data, provide a concise, 3-bullet point summary of the key insights, trends, and potential areas for improvement.

Focus on the most significant data points. For example, highlight large discrepancies between created and completed tasks, identify the most frequent problem categories, or point out significant trends over time. Also consider the workload distribution among assignees and identify any prolific reporters, as this could indicate training needs or areas with recurring issues.

Do not just list the data; interpret it.

**${timeFrame} Data:**
${JSON.stringify(data, null, 2)}

**Analysis Summary:**`;

        const response = await ai.models.generateContent({
            model: "gemini-2.5-flash",
            contents: prompt,
        });
        
        return response.text.trim();
    } catch (error) {
        console.error("Error calling Gemini API for analytics summarization:", error);
        throw new Error("Failed to generate an analytics summary. The AI service may be temporarily unavailable.");
    }
}